import { createElement, useMemo } from "react";
import { PhoneIcon, QueueListIcon } from "@heroicons/react/24/outline";

import {
  AccountHistoryCard,
  AccountHistoryCardBasePropsI,
} from "shared/ui/activity-log/cards-v2/base";
import { HistoryCardCallRecording } from "shared/ui/activity-log/cards-v2/base/call-recording";
import { HistoryCardCallNotes } from "shared/ui/activity-log/cards-v2/base/notes";
import { HistoryCardActivityType } from "shared/ui/activity-log/cards-v2/base/activity-type";
import { HistoryCardContactInfo } from "shared/ui/activity-log/cards-v2/base/contact-info";
import { HistoryCardCallerInfo } from "shared/ui/activity-log/cards-v2/base/caller-info";
import {
  HistoryCardBookedMeetingInfo,
  HistoryCardBookedMeetingOriginalInfo,
} from "shared/ui/activity-log/cards-v2/base/event-date";
import { HistoryCardAEInfo } from "shared/ui/activity-log/cards-v2/base/ae-info";
import { HistoryCardStatusChanges } from "shared/ui/activity-log/cards-v2/base/status-changes";
import {
  DISPOSITIONS,
  DISPOSITIONS_STRING_MAP_GLENX_V2,
  DISPOSITIONS_STRING_MAP_V2,
} from "shared/lib/constants/dispositions";
import { AccountHistoryCardQualificationCriteria } from "shared/ui/activity-log/cards-v2/base/qualification-criteria";
import { AccountHistoryCardQualificationCriteriaPreCheckAuditResult } from "shared/ui/activity-log/cards-v2/base/qualification-criteria-pre-check-audit-result";
import { clsxMerge, getS3AssetPath } from "shared/lib/helpers";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { HistoryCardCallSummary } from "./base/call-summary";

const MeetingQualifiedIcon = ({ className }: PropsWithClassNameI) => (
  <img
    alt="Meeting Qualified"
    className={clsxMerge(className, "h-8 w-8")}
    src={getS3AssetPath("platform/activity-logs/meeting-qualified.webp")}
  />
);

const PMS_TYPES_TO_SHOW_QC_SUMMARY = [
  DISPOSITIONS.NOT_QUALIFIED,
  DISPOSITIONS.MEETING_QUALIFIED,
  DISPOSITIONS.MEETING_NOT_QUALIFIED,
] as const;

const ACTIVITY_TYPE_DEFAULT_CONFIG = {
  icon: PhoneIcon,
  title: "Booked meeting",
};

const HistoryCardBookedMeetingV2 = ({
  isGlenXCampaign,
  activity,
  addons,
}: AccountHistoryCardBasePropsI) => {
  const meetingDisposition = activity?.meeting_disposition;
  const customDisposition = activity.custom_disposition;

  const shouldShowQualificationCriteria =
    meetingDisposition &&
    PMS_TYPES_TO_SHOW_QC_SUMMARY.includes(activity.meeting_disposition as any);

  const isQualifiedMeeting =
    meetingDisposition === DISPOSITIONS.MEETING_QUALIFIED;

  const activityTypeConfig = useMemo(() => {
    if (meetingDisposition) {
      const labelsMap = isGlenXCampaign
        ? DISPOSITIONS_STRING_MAP_GLENX_V2
        : DISPOSITIONS_STRING_MAP_V2;

      let label = labelsMap[meetingDisposition];

      if (
        isQualifiedMeeting &&
        !isGlenXCampaign &&
        (activity.is_payout_pending || activity.is_disputed)
      ) {
        label = "Meeting Shown";
      }

      return {
        icon: isQualifiedMeeting ? MeetingQualifiedIcon : QueueListIcon,
        isSuccess: isQualifiedMeeting,
        isWarning: meetingDisposition === DISPOSITIONS.MEETING_NO_SHOW,
        title: label,
      };
    }

    return {
      ...ACTIVITY_TYPE_DEFAULT_CONFIG,
      title: customDisposition
        ? customDisposition.name
        : ACTIVITY_TYPE_DEFAULT_CONFIG.title,
    };
  }, [
    meetingDisposition,
    isGlenXCampaign,
    customDisposition,
    isQualifiedMeeting,
    activity.is_disputed,
    activity.is_payout_pending,
  ]);

  const isRescheduled = useMemo(
    () => meetingDisposition === DISPOSITIONS.MEETING_RESCHEDULED,
    [meetingDisposition]
  );

  return (
    <AccountHistoryCard
      createdAt={activity.created_at}
      className={clsxMerge({
        "border-success-content": isQualifiedMeeting,
      })}
    >
      <div className="flex flex-col gap-4">
        <HistoryCardActivityType config={activityTypeConfig} />

        <HistoryCardBookedMeetingInfo
          isBold={!meetingDisposition || isRescheduled}
          date={activity.follow_up_time}
        />

        {isRescheduled && (
          <HistoryCardBookedMeetingOriginalInfo
            date={activity.original_meeting_time}
          />
        )}

        {addons?.BookedMeetingEvent &&
          createElement(addons.BookedMeetingEvent, { activityLog: activity })}

        <HistoryCardContactInfo contact={activity.contact} />
        <HistoryCardCallerInfo callerName={activity.caller_name || "N/A"} />
        <HistoryCardAEInfo
          aeName={activity.ae_name || "N/A"}
          isGlenXCampaign={isGlenXCampaign}
        />

        {/*
          Caller and AE above describe who BOOKED the meeting. Everything that
          happened to it afterwards — cancel, reschedule, show / no-show,
          dispute — is attributed here instead.
        */}
        <HistoryCardStatusChanges statusChanges={activity.status_changes} />
      </div>

      <div className="flex flex-col gap-4">
        {!!activity.call_recording_url?.recording_url && (
          <HistoryCardCallRecording
            recordingURL={activity.call_recording_url?.recording_url}
            transcriptURL={activity.call_transcript_url}
            hadDiscussion={!!activity.call_has_discussion}
          />
        )}

        {!!activity.call_summary_url && (
          <HistoryCardCallSummary summaryURL={activity.call_summary_url} />
        )}

        {shouldShowQualificationCriteria && (
          <AccountHistoryCardQualificationCriteria
            qualificationCriteriaV2={activity?.qualification_criteria_v2}
            qualificationCriteria={activity?.qualification_criteria}
          />
        )}

        <AccountHistoryCardQualificationCriteriaPreCheckAuditResult
          auditResult={activity.qc_pre_check_audit_result}
        />

        <HistoryCardCallNotes
          className={clsxMerge(shouldShowQualificationCriteria && "mt-2")}
          isCustomerNotes={activity.is_customer}
          isGlenXCampaign={isGlenXCampaign}
          notes={activity.notes}
        />
      </div>
    </AccountHistoryCard>
  );
};

export default HistoryCardBookedMeetingV2;
